<template>
  <div
    class="flex justify-center align-middle min-h-screen overflow-x-hidden
      bg-center bg-fixed bg-repeat py-28 px-16 items-center"
  >
    <div class="card flex flex-col justify-center items-center p-5 w-full max-w-[36rem]">
      <!-- Logo -->
      <img
        class="block mx-auto mb-6 w-40 h-auto"
        src="@/assets/omind_logo.png"
        alt="Omind"
      />

      <!-- Error Icon -->
      <div class="error-icon-wrapper mb-4">
        <i v-if="errorType === 'TENANT_NOT_FOUND'" class="error-icon">
          <font-awesome-icon
            icon="fa-solid fa-circle-xmark"
            class="text-[var(--color-error)] text-6xl"
          />
        </i>
        <i v-else-if="errorType === 'TENANT_SUSPENDED'" class="error-icon">
          <font-awesome-icon
            icon="fa-solid fa-circle-pause"
            class="text-[var(--color-orange)] text-6xl"
          />
        </i>
        <i v-else-if="errorType === 'TENANT_PROVISIONING'" class="error-icon">
          <font-awesome-icon
            icon="fa-solid fa-circle-notch"
            class="text-[var(--color-orange)] text-6xl animate-spin"
          />
        </i>
        <i v-else class="error-icon">
          <font-awesome-icon
            icon="fa-solid fa-circle-exclamation"
            class="text-[var(--color-error)] text-6xl"
          />
        </i>
      </div>

      <!-- Error Title -->
      <h2 class="text-center pb-3 text-2xl font-bold">
        {{ errorTitle }}
      </h2>

      <!-- Error Message -->
      <p class="text-center text-lg text-gray-600 mb-4">
        {{ errorMessage }}
      </p>

      <!-- Tenant Info -->
      <div v-if="tenantSlug" class="tenant-info-box mb-6">
        <span class="text-gray-500 text-sm">Requested workspace:</span>
        <code class="tenant-slug-badge">{{ tenantSlug }}</code>
      </div>

      <!-- Support Info -->
      <div class="text-center text-sm text-gray-500 mb-6">
        <p>
          If you believe this is an error, please contact your administrator
          or our support team.
        </p>
        <a
          href="mailto:support@omind.ai"
          class="text-[var(--color-orange)] hover:text-[var(--color-fulvous)]
            transition duration-500 font-bold mt-2 inline-block"
        >
          support@omind.ai
        </a>
      </div>

      <!-- Action Button -->
      <Button
        v-if="errorType === 'TENANT_PROVISIONING'"
        type="button"
        class="mt-4 px-12 py-3 text-xl text-white bg-[var(--color-orange)]
          hover:bg-[var(--color-fulvous)] mx-auto rounded-full
          transition duration-500"
        unstyled
        @click="refresh"
      >
        <font-awesome-icon icon="fa-solid fa-rotate-right" class="mr-2" />
        Try Again
      </Button>
    </div>
  </div>
</template>

<script>
import Button from 'primevue/button';

export default {
  name: 'TenantErrorView',
  components: {
    Button,
  },
  props: {
    errorType: {
      type: String,
      default: 'TENANT_NOT_FOUND',
      validator: (value) => [
        'TENANT_NOT_FOUND',
        'TENANT_SUSPENDED',
        'TENANT_DELETED',
        'TENANT_PROVISIONING',
        'VALIDATION_ERROR',
      ].includes(value),
    },
    tenantSlug: {
      type: String,
      default: '',
    },
    customMessage: {
      type: String,
      default: '',
    },
  },
  computed: {
    errorTitle() {
      const titles = {
        TENANT_NOT_FOUND: 'Workspace Not Found',
        TENANT_SUSPENDED: 'Workspace Suspended',
        TENANT_DELETED: 'Workspace Unavailable',
        TENANT_PROVISIONING: 'Workspace Being Set Up',
        VALIDATION_ERROR: 'Something Went Wrong',
      };
      return titles[this.errorType] || 'Error';
    },
    errorMessage() {
      if (this.customMessage) return this.customMessage;

      const messages = {
        TENANT_NOT_FOUND: 'The workspace you are trying to access does not '
          + 'exist. Please check the URL and try again.',
        TENANT_SUSPENDED: 'This workspace has been temporarily suspended. '
          + 'Please contact your administrator for more information.',
        TENANT_DELETED: 'This workspace is no longer available.',
        TENANT_PROVISIONING: 'Your workspace is currently being set up. '
          + 'This usually takes just a few moments. Please try again shortly.',
        VALIDATION_ERROR: 'We encountered an issue while loading your '
          + 'workspace. Please try again later.',
      };
      return messages[this.errorType] || 'An unexpected error occurred.';
    },
  },
  methods: {
    refresh() {
      window.location.reload();
    },
  },
};
</script>

<style scoped>
.tenant-info-box {
  display: flex;
  flex-direction: column;
  align-items: center;
  gap: 0.5rem;
  background: rgba(0, 0, 0, 0.03);
  padding: 1rem 1.5rem;
  border-radius: 12px;
}

.tenant-slug-badge {
  background: var(--color-orange);
  color: white;
  padding: 0.25rem 0.75rem;
  border-radius: 6px;
  font-family: monospace;
  font-size: 0.9rem;
}

.error-icon-wrapper {
  display: flex;
  justify-content: center;
}
</style>
